import { useEffect, useState } from 'react';
import { addDays, differenceInSeconds, isAfter } from 'date-fns';
import Cookies from 'js-cookie';

const MAX_TIMESTAMP = 8640000000000000;

// The server sends down the absolute end date for the discount.
// We never want to show the user more time than they actually have.
// But, we do show them 2 days left even if they actually have more time,
// the timer will just reset at the end of the two day period if there's more time left.
//
// So, the first load, we check if the server date is before 2 days from now.
// If it is, we just count down to the server date.
// If it's not, we count down to two days from now.
// We store this initial "2 days from now" date in local storage,
// so if you come back the next day, the timer will only have a day left.
// But, if you come back 3 days later, and there's still more than 2 days
// before the server end date for the discount,
// we just reset the timer to count down to 2 days again.
const useDiscountDuration = (serverEndDateString?: string) => {
  const domain = window.location.hostname.replace(/^[^.]+\./g, '');

  serverEndDateString = serverEndDateString ?? '';
  const [duration, setDuration] = useState(0);

  const setCookie = (date: string) => {
    Cookies.set('unibrow_discount_end_date', date, {
      expires: 2,
      domain: domain,
    });
  };

  useEffect(() => {
    // For backwards compatiblity with localStorage
    // If the end date is in localStorage write that to a cookie and clear it
    const localStorageEndDate = localStorage.getItem(
      'unibrow_discount_end_date',
    );
    if (localStorageEndDate) {
      localStorage.removeItem('unibrow_discount_end_date');
      setCookie(localStorageEndDate);
    }

    const endDateString = Cookies.get('unibrow_discount_end_date');

    let serverEndDate: Date;
    if (!serverEndDateString) {
      // If we get no date from server, just assume we're good to run the timer
      // for the next two days.
      serverEndDate = new Date(MAX_TIMESTAMP);
    } else if (serverEndDateString.includes('/')) {
      const [serverMonth, serverDay] = serverEndDateString
        .split('/')
        .map(Number);
      // Date constructor expects the month to be 0-11,
      // but the day should be 1-31. Go figure.
      serverEndDate = new Date(
        new Date().getFullYear(),
        serverMonth - 1,
        serverDay,
      );
    } else {
      const timestamp = parseInt(serverEndDateString);
      serverEndDate = new Date(timestamp * 1000);
    }

    const defaultEndDate = addDays(new Date(), 2);
    const serverEndDateOutOfBounds: boolean =
      serverEndDate < new Date() || serverEndDate > defaultEndDate;
    const newEndDate: Date = serverEndDateOutOfBounds
      ? defaultEndDate
      : serverEndDate;

    // endDateString is null if it's the first time seeing unibrow, no string in local storage
    // endDateString is "Invalid Date" if something got messed up during debugging :(
    if (!endDateString || endDateString.includes('Invalid')) {
      setCookie(newEndDate.toString());
      setDuration(differenceInSeconds(newEndDate, new Date()));
    } else {
      const cookieEndDate: Date = new Date(endDateString);

      // Is right now past the stored date? If so, reset it.
      if (isAfter(new Date(), cookieEndDate)) {
        setCookie(newEndDate.toString());
        setDuration(differenceInSeconds(newEndDate, new Date()));
        // If the server end date comes sooner than the stored date, use that
      } else if (isAfter(cookieEndDate, newEndDate)) {
        setCookie(serverEndDate.toString());
        setDuration(differenceInSeconds(newEndDate, new Date()));
        // Otherwise, just used the stored date for the duration
      } else {
        setDuration(differenceInSeconds(cookieEndDate, new Date()));
      }
    }
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return duration;
};

export default useDiscountDuration;
